/**
 * localStorage utilities with TTL (time-to-live).
 * Keys are stored with prefix "ttl_" and an expiry timestamp.
 * Use setWithExpiry(key, value, ttlMs) to store; expired keys are cleaned automatically.
 */

const TTL_PREFIX = 'ttl_'
const TTL_CLEANUP_INTERVAL_MS = 2 * 60 * 60 * 1000 // 2 hours

/**
 * Remove expired ttl_ keys from localStorage only.
 * Do not clear auth 'user' key — that is managed by authService (login/logout/auth/me).
 */
export const cleanExpiredKeys = () => {
  if (typeof window === 'undefined' || !window.localStorage) return
  const now = Date.now()
  Object.keys(localStorage).forEach((key) => {
    if (!key.startsWith(TTL_PREFIX)) return
    try {
      const item = JSON.parse(localStorage.getItem(key))
      if (!item?.expiry || now > item.expiry) {
        localStorage.removeItem(key)
      }
    } catch {
      localStorage.removeItem(key)
    }
  })
}

/**
 * Save a value with TTL. After ttlMs milliseconds the key is considered expired and will be removed by cleanExpiredKeys.
 * @param {string} key - Storage key (will be stored as ttl_${key})
 * @param {any} value - Value to store (will be JSON.stringify'd)
 * @param {number} ttl - Time-to-live in milliseconds
 */
export const setWithExpiry = (key, value, ttl) => {
  if (typeof window === 'undefined' || !window.localStorage) return
  const item = {
    value,
    expiry: Date.now() + ttl
  }
  localStorage.setItem(`${TTL_PREFIX}${key}`, JSON.stringify(item))
}

/**
 * Get a value stored with setWithExpiry. Returns null if missing or expired.
 * @param {string} key - Storage key (without ttl_ prefix)
 * @returns {any|null}
 */
export const getWithExpiry = (key) => {
  if (typeof window === 'undefined' || !window.localStorage) return null
  const raw = localStorage.getItem(`${TTL_PREFIX}${key}`)
  if (!raw) return null
  try {
    const item = JSON.parse(raw)
    if (!item?.expiry || Date.now() > item.expiry) {
      localStorage.removeItem(`${TTL_PREFIX}${key}`)
      return null
    }
    return item.value
  } catch {
    localStorage.removeItem(`${TTL_PREFIX}${key}`)
    return null
  }
}

/**
 * Start automatic cleanup: does NOT run on page load. First cleanup after TTL_CLEANUP_INTERVAL_MS,
 * then every TTL_CLEANUP_INTERVAL_MS. So nothing is erased on first load.
 * @returns {() => void} Cleanup function (clear timeout and interval)
 */
export const startTTLCleanup = () => {
  let intervalId = null
  const timeout = setTimeout(() => {
    cleanExpiredKeys()
    intervalId = setInterval(cleanExpiredKeys, TTL_CLEANUP_INTERVAL_MS)
  }, TTL_CLEANUP_INTERVAL_MS)
  return () => {
    clearTimeout(timeout)
    if (intervalId) clearInterval(intervalId)
  }
}

export { TTL_CLEANUP_INTERVAL_MS, TTL_PREFIX }
